<template lang="html">
    <div class="hydrologic-variability-tables">
        <div
            v-for="(firstColumnValues, firstColumn, firstColumnIndex) in data"
            :key="firstColumnIndex"
            class="year-table no-page-break"
            :class="`table-${firstColumnIndex + 1 + indexAdjustment}`"
        >
            <div class="columns months-row">
                <div
                    class="column column-row-title"
                    :class="{ 'year-range-title-width is-2': hasCandidates }"
                >
                    <span>
                        &nbsp;
                    </span>
                </div>
                <div
                    class="column percentile"
                    :class="{'percentile-width is-1': hasCandidates}"
                >
                    <span>
                        &nbsp;
                    </span>
                </div>
                <div
                    v-for="month in 12"
                    :key="month"
                    class="column month-value"
                >
                    <span>
                        {{ monthNumberToName(month) }}
                    </span>
                </div>
            </div>
            <div
                v-for="(percentileValues, percentile, percentileIndex) in firstColumnValues"
                :key="percentileIndex"
                class="columns"
                :class="classObject(percentileIndex)"
            >
                <div
                    class="column year-range-title"
                    :class="{ 'year-range-title-width is-2': hasCandidates }"
                >
                    <span v-if="firstRowCandidate(percentileIndex)">
                        Watershed
                    </span>
                    <span v-else-if="percentileIndex === indexAdjustment">
                        {{ firstColumn }}
                    </span>
                    <span v-else>
                        &nbsp;
                    </span>
                </div>

                <div
                    class="column percentile"
                    :class="{'percentile-width is-1': hasCandidates}"
                >
                    <span v-if="firstRowCandidate(percentileIndex)">
                        &nbsp;
                    </span>
                    <!-- replace 50th percentile with 'Mean' -->
                    <span v-else-if="percentile === '50th' && meanData">
                        Mean
                    </span>
                    <span v-else>
                        {{ percentile }}
                    </span>
                </div>
                <div
                    v-for="(monthlyValues, month, monthIndex) in percentileValues"
                    :key="monthIndex"
                    class="column month-value"
                >
                    <span v-if="firstRowCandidate(percentileIndex)">
                        {{ monthlyValues }}
                    </span>
                    <!-- replace 50th percentile data with 'Mean' data -->
                    <span v-else-if="percentileIndex === 3 && meanData">
                        {{ meanData[monthIndex] | numFormat('0.00') }}
                    </span>
                    <span v-else>
                        {{ monthlyValues | numFormat('0.00') }}
                    </span>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { monthNumberToName } from './helpers';

export default {
    name: 'HydrologicVariabilityTable',

    props: {
        data: {
            type: Object,
            required: true,
        },
        hasCandidates: {
            type: Boolean,
            required: false,
            default: false,
        },
        meanData: {
            type: Array,
            required: false,
            default: null,
        },
    },

    computed: {
        indexAdjustment() {
            return this.hasCandidates ? 1 : 0;
        },
    },

    methods: {
        /**
         * adds conditional css classes to change row highlighting color
         * @param {Number} percentileIndex Current index of percentile from 'percs' array
         */
        classObject(percentileIndex) {
            return {
                // changes the background color to a lighter color for the 90th and 10th percentiles.
                accent: percentileIndex === this.indexAdjustment
                    || percentileIndex === 4 + this.indexAdjustment,
                // highlight the first row as a header row if hasCandidates prop is true
                'candidate-row': percentileIndex === 0 && this.hasCandidates,
            };
        },

        /**
         * checks if first percentile and if hasCandidates is present
         * @param {Number} percentileIndex Current index of percentile
         * @returns {Boolean}
         */
        firstRowCandidate(percentileIndex) {
            return this.hasCandidates && percentileIndex === 0;
        },

        monthNumberToName,
    },
};
</script>

<style lang="scss">
.hydrologic-variability-tables {
    $color-hydrovar-1: #32429b !default;
    $color-hydrovar-1-accent: #7a85c1 !default;
    $color-hydrovar-2: #8f3d96 !default;
    $color-hydrovar-2-accent: #c694c3 !default;
    $color-hydrovar-3: #418ecc !default;
    $color-hydrovar-3-accent: #95c8ec !default;
    $color-hydrovar-4: #1abcd4 !default;
    $color-hydrovar-4-accent: #88d3e2 !default;
    $color-hydrovar-candidate-row: #b1b1b1 !default;

    .year-table {
        $row-height: 17px;

        font-size: 12px;
        line-height: $row-height;
        margin-bottom: 2rem;
        color: #fff;

        // year range colors
        &.table-1 {
            background: $color-hydrovar-1;

            .accent {
                background: $color-hydrovar-1-accent;
            }
        }

        &.table-2 {
            background: $color-hydrovar-2;

            .accent {
                background: $color-hydrovar-2-accent;
            }
        }

        &.table-3 {
            background: $color-hydrovar-3;

            .accent {
                background: $color-hydrovar-3-accent;
            }
        }

        &.table-4 {
            background: $color-hydrovar-4;

            .accent {
                background: $color-hydrovar-4-accent;
            }
        }

        .months-row {
            background: darken($color-hydrovar-candidate-row, 10%);
        }

        .candidate-row {
            background: $color-hydrovar-candidate-row;
        }

        .columns {
            margin: 0;

            .column {
                padding: 0;
                text-align: center;

                // line up the month columns with hydro var chart
                &.year-range-title-width {
                    width: 11rem;
                }

                // match the label width of the hydro var chart
                &.percentile-width {
                    width: calc(0.5em + 63px);
                }
            }
        }
    }
}

</style>
